// WP-17 (US 6.2, LLD-05 O-02) — NotificationsVM. The persistent notification
// store the office bell polls (GET /notifications), replacing WP-26's feed-derived
// bell + localStorage "last seen" (D-WP26-1). Unread state is now server-side
// (readAt), cross-device. Marking read is a mutation that refreshes the feed.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiClient, type ApiClient } from "../../api/api-client";
import type { NotificationDto, NotificationFeedDto } from "./notifications-contracts";

export const notificationKeys = {
  all: ["notifications"] as const,
};

/** Poll the feed (owner/office manager). Mirrors the office completion alert. */
export function useNotifications(
  enabled: boolean,
  api: ApiClient = apiClient,
): UseQueryResult<NotificationFeedDto> {
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: () => api.get<NotificationFeedDto>("/notifications"),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useMarkAllNotificationsRead(
  api: ApiClient = apiClient,
): UseMutationResult<{ updated: number }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updated: number }>("/notifications/read-all"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkNotificationRead(
  api: ApiClient = apiClient,
): UseMutationResult<NotificationDto, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NotificationDto>(`/notifications/${id}/read`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
